import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

export interface WidgetDefinition {
  type: string;
  title: string;
  description?: string;
  icon?: string;
  defaultSize?: { cols: number; rows: number };
  defaultDisplayType?: string;
  defaultFields?: string[];
}

/**
 * Consolidated service for widget configuration and data adaptation
 * Replaces the old WidgetConfigService and DashboardAdapterService
 */
@Injectable({
  providedIn: 'root'
})
export class WidgetAdapterService {
  private widgetDefinitions: WidgetDefinition[] = [
    {
      type: 'status',
      title: 'Status',
      description: 'Shows completion status for users',
      icon: 'pie_chart',
      defaultSize: { cols: 2, rows: 2 },
      defaultDisplayType: 'chart',
      defaultFields: ['percentComplete', 'totalUsers', 'completedUsers']
    },
    {
      type: 'upcomingExercises',
      title: 'Upcoming Exercises',
      description: 'Shows upcoming exercises and training',
      icon: 'event',
      defaultSize: { cols: 4, rows: 2 },
      defaultDisplayType: 'table',
      defaultFields: ['name', 'date', 'location']
    },
    {
      type: 'userProgress',
      title: 'Bruger Fremgang',
      description: 'Viser bruger fremgang og status',
      icon: 'person',
      defaultSize: { cols: 6, rows: 4 },
      defaultDisplayType: 'table',
      defaultFields: ['name', 'path', 'percentComplete', 'station']
    },
    {
      type: 'userProgressStats',
      title: 'Bruger Statistik',
      description: 'Viser detaljeret bruger statistik og fremgang',
      icon: 'bar_chart',
      defaultSize: { cols: 6, rows: 4 },
      defaultDisplayType: 'chart',
      defaultFields: ['name', 'path', 'percentComplete', 'station', 'trend', 'lastActivity']
    },
    {
      type: 'locationProgress',
      title: 'Lokation Fremgang',
      description: 'Viser fremgang for alle lokationer',
      icon: 'location_on',
      defaultSize: { cols: 6, rows: 4 },
      defaultDisplayType: 'table',
      defaultFields: ['name', 'userCount', 'averageProgress', 'completionRate']
    },
    {
      type: 'simpleNumber',
      title: 'Simpelt Tal',
      description: 'Viser et enkelt tal med titel',
      icon: 'looks_one',
      defaultSize: { cols: 3, rows: 2 },
      defaultDisplayType: 'number',
      defaultFields: ['value', 'title']
    },
    {
      type: 'cateringSummary',
      title: 'Forplejningsbestilling',
      description: 'Opsummering af forplejningsbehov for kommende events',
      icon: 'restaurant',
      defaultSize: { cols: 12, rows: 6 },
      defaultDisplayType: 'list',
      defaultFields: ['event_name', 'preferences', 'extras']
    },
    {
      type: 'eventsCalendar',
      title: 'Begivenhedskalender',
      description: 'Kalender over begivenheder med samme filtre som begivenhedssiden',
      icon: 'calendar_month',
      defaultSize: { cols: 12, rows: 6 },
      defaultDisplayType: 'calendar',
      defaultFields: []
    },
    {
      type: 'userStatus',
      title: 'User Status',
      description: 'Shows educational path status for users',
      icon: 'account_box',
      defaultSize: { cols: 6, rows: 4 },
      defaultDisplayType: 'table',
      defaultFields: ['firstName', 'lastName', 'path', 'status', 'completedPercent']
    },
    {
      type: 'yearComparison',
      title: 'Year Comparison',
      description: 'Compares data between current and previous year',
      icon: 'bar_chart',
      defaultSize: { cols: 6, rows: 3 },
      defaultDisplayType: 'chart',
      defaultFields: ['label', 'currentYear', 'previousYear']
    },
    {
      type: 'quickLinks',
      title: 'Quick Links',
      description: 'Shows quick access links',
      icon: 'link',
      defaultSize: { cols: 2, rows: 2 },
      defaultDisplayType: 'list',
      defaultFields: ['title', 'url', 'icon']
    },
    {
      type: 'numberStats',
      title: 'Number Stats',
      description: 'Shows key statistics',
      icon: 'insert_chart',
      defaultSize: { cols: 2, rows: 2 },
      defaultDisplayType: 'stat',
      defaultFields: ['value', 'title', 'secondaryValue']
    }
  ];

  constructor() {
    // Log available widget types on service initialization
  }

  /**
   * Get all available widget types
   */
  getAllWidgetTypes(): string[] {
    const types = this.widgetDefinitions.map(def => def.type);
    return types;
  }

  /**
   * Get widget configuration for a specific type
   */
  getWidgetConfig(type: string): WidgetDefinition {
    const config = this.widgetDefinitions.find(def => def.type === type);
    if (!config) {
      console.warn(`[WidgetAdapterService] No configuration found for widget type: ${type}`);
      // Return default config if not found
      return {
        type: type,
        title: type,
        defaultSize: { cols: 2, rows: 2 },
        defaultDisplayType: 'table',
        defaultFields: ['name', 'value']
      };
    }
    return config;
  }

  /**
   * Get default display type for a widget type
   */
  getDefaultDisplayType(type: string): string {
    const config = this.getWidgetConfig(type);
    return config.defaultDisplayType || 'table';
  }

  /**
   * Get default fields for a widget type
   */
  getDefaultFields(type: string): string[] {
    const config = this.getWidgetConfig(type);
    return config.defaultFields || ['name', 'value'];
  }

  /**
   * Get default widget size for a specific widget type
   */
  getDefaultWidgetSize(type: string): { cols: number; rows: number } {
    const config = this.getWidgetConfig(type);
    
    // Special case for userProgress widgets - ensure they get proper size
    if (type === 'userProgress') {
      return { cols: 6, rows: 4 };
    }
    
    return config.defaultSize || { cols: 2, rows: 2 };
  }

  /**
   * Convert a DashboardConfigDTO to an array of WidgetData
   */
  convertDashboardConfigDTOToWidgetDataArray(dashboard: any): any[] {
    if (!dashboard || !dashboard.widgets) {
      return [];
    }
    
    // Log incoming widget size
    
    return dashboard.widgets.map((widget: any) => {
      // Ensure there's a valid type, use userProgress as default if missing
      const type = widget.widgetType || 'userProgress';
      
      // Get default size from widget config if size is missing
      const defaultSize = this.getWidgetConfig(type)?.defaultSize || { cols: 2, rows: 2 };
      
      // Get size from position data (backend format) or size property (frontend format)
      let cols = widget.position?.cols || widget.size?.cols;
      let rows = widget.position?.rows || widget.size?.rows;
      
      // Use default size if no size specified
      if (!cols || !rows) {
        cols = defaultSize.cols || 2;
        rows = defaultSize.rows || 2;
      } else {
      }

      // Ensure we have the widget's filters available
      const widgetFilters = widget.defaultFilters || {};
      
      const widgetData = {
        id: widget.id,
        type: type,
        title: widget.title || `Widget ${widget.id}`,
        position: {
          x: widget.position?.x || 0,
          y: widget.position?.y || 0,
          cols: cols,
          rows: rows,
          id: widget.id
        },
        x: widget.position?.x || 0,
        y: widget.position?.y || 0,
        cols: cols,
        rows: rows,
        dataSource: widget.dataSource || 'default',
        defaultFilters: widgetFilters,
        settings: {
          category: type,
          filters: widgetFilters,
          fields: this.getDefaultFields(type),
          displayType: this.getDefaultDisplayType(type)
        }
      };
      
      
      return widgetData;
    });
  }
} 